import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { InstructionSet, fetchInstructionSets, rollbackToVersion } from '../services/instructionSetService';
import { deleteInstructionSet } from '../services/instructionSetService';
import { getConnectionId } from '../utils/storage';
import { generateDomainKnowledge, DomainKnowledgeResponse } from '../services/databaseService';

interface InstructionSetsListProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
}

const InstructionSetsList: React.FC<InstructionSetsListProps> = ({ isOpen, onClose, onBack }) => {
  const navigate = useNavigate();
  const [instructionSets, setInstructionSets] = useState<InstructionSet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSet, setSelectedSet] = useState<InstructionSet | null>(null);
  const [activatingVersion, setActivatingVersion] = useState<number | null>(null);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [newInstructions, setNewInstructions] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);

  // State for delete in progress
  const [deletingVersion, setDeletingVersion] = useState<number | null>(null);

  // Handler for deleting instruction set
  const handleDeleteVersion = async (connectionId: string, version: number) => {
    setDeletingVersion(version);
    setError(null);
    try {
      await deleteInstructionSet(connectionId, version);
      await loadInstructionSets();
      setSelectedSet(null);
    } catch (error) {
      console.error('Error deleting instruction set:', error);
      setError(`Failed to delete version ${version}. Please try again.`);
    } finally {
      setDeletingVersion(null);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadInstructionSets();
    }
  }, [isOpen]);

  const loadInstructionSets = async () => {
    const connectionId = getConnectionId();
    if (!connectionId) {
      setError('No connection ID found. Please configure database connection first.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchInstructionSets(connectionId);
      setInstructionSets(response.items);
    } catch (error) {
      console.error('Error loading instruction sets:', error);
      setError('Failed to load instruction sets. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDomainKnowledge = (data: DomainKnowledgeResponse): string => {
    const { instruction_set } = data;

    let formatted = `=== SQL Instruction Set v${instruction_set.version} ===\n\n`;

    // SQL Rules
    formatted += `SQL Safety Rules:\n`;
    formatted += `- Allowed operations: ${instruction_set.sql_rules.allowed_operations.join(', ')}\n`;
    formatted += `- Forbidden operations: ${instruction_set.sql_rules.forbidden_operations.join(', ')}\n`;
    formatted += `- SELECT * forbidden: ${instruction_set.sql_rules.no_select_star ? 'Yes' : 'No'}\n`;
    formatted += `- Default LIMIT: ${instruction_set.sql_rules.default_limit}\n`;
    formatted += `- Avoid sensitive columns: ${instruction_set.sql_rules.avoid_sensitive_columns.join(', ')}\n\n`;

    // Join Rules
    formatted += `Join Rules:\n`;
    formatted += `- Use foreign keys for joins: ${instruction_set.join_rules.use_foreign_keys_for_joins ? 'Yes' : 'No'}\n`;
    formatted += `- Join type preference: ${instruction_set.join_rules.join_type_preference}\n`;
    formatted += `- Max join depth: ${instruction_set.join_rules.max_join_depth}\n\n`;
    formatted += `\n`;

    // Column Roles
    formatted += `Column Role Hints:\n`;
    formatted += `- Metric patterns: ${instruction_set.column_roles.metric_patterns.join(', ')}\n`;
    formatted += `- Timestamp patterns: ${instruction_set.column_roles.timestamp_patterns.join(', ')}\n`;
    formatted += `- Category patterns: ${instruction_set.column_roles.category_patterns.join(', ')}\n`;
    formatted += `- Identifier patterns: ${instruction_set.column_roles.identifier_patterns.join(', ')}\n`;
    formatted += `\n`;

    // Query Structure Rules
    formatted += `Query Structure Rules:\n`;
    formatted += `- Always use aliases: ${instruction_set.query_structure_rules.always_use_aliases ? 'Yes' : 'No'}\n`;
    formatted += `- GROUP BY required for aggregates: ${instruction_set.query_structure_rules.group_by_required_for_aggregates ? 'Yes' : 'No'}\n`;
    formatted += `- Limit large results: ${instruction_set.query_structure_rules.limit_large_results ? 'Yes' : 'No'}\n\n`;

    // NL Query Rules
    formatted += `Natural Language Query Rules:\n`;
    formatted += `- Aggregation keywords: ${instruction_set.nl_query_rules.aggregation_keywords.join(', ')}\n`;
    formatted += `- Trend keywords: ${instruction_set.nl_query_rules.trend_keywords.join(', ')}\n`;
    formatted += `- Comparison keywords: ${instruction_set.nl_query_rules.comparison_keywords.join(', ')}\n`;
    formatted += `- List keywords: ${instruction_set.nl_query_rules.list_keywords.join(', ')}\n`;

    return formatted;
  };

  const handleGenerateAI = async () => {
    const connectionId = getConnectionId();
    if (!connectionId) {
      setError('No connection ID found. Please configure database connection first.');
      return;
    }

    setIsGeneratingAI(true);
    setError(null);

    try {
      const domainData = await generateDomainKnowledge(connectionId);
      const formattedText = formatDomainKnowledge(domainData);
      setNewInstructions(formattedText);
    } catch (error) {
      console.error('Error generating AI instructions:', error);
      setError('Failed to generate instructions from AI. Please try again.');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const handleActivateVersion = async (version: number) => {
    const connectionId = getConnectionId();
    if (!connectionId) {
      setError('No connection ID found. Please configure database connection first.');
      return;
    }

    setActivatingVersion(version);
    setError(null);

    try {
      await rollbackToVersion(connectionId, version);
      // Refresh the list to show the updated active status
      await loadInstructionSets();
      setSelectedSet(null); // Close expanded view
    } catch (error) {
      console.error('Error activating version:', error);
      setError(`Failed to activate version ${version}. Please try again.`);
    } finally {
      setActivatingVersion(null);
    }
  };

  const handleCreateNew = () => {
    setShowCreatePopup(true);
    setNewInstructions('');
    setError(null);
  };

  const handleSubmitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newInstructions.trim()) {
      setError('Instructions are required');
      return;
    }

    const connectionId = getConnectionId();
    if (!connectionId) {
      setError('No connection ID found. Please configure database connection first.');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      // Use the existing saveLLMInstructions function from databaseService
      const { saveLLMInstructions } = await import('../services/databaseService');
      await saveLLMInstructions(connectionId, newInstructions.trim());
      
      // Refresh the list to show the new instruction set
      await loadInstructionSets();
      
      // Close the popup
      setShowCreatePopup(false);
      setNewInstructions('');
    } catch (error) {
      console.error('Error creating instruction set:', error);
      setError('Failed to create instruction set. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const truncateInstructions = (instructions: string, maxLength: number = 100) => {
    if (instructions.length <= maxLength) return instructions;
    return instructions.substring(0, maxLength) + '...';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg w-full max-w-4xl max-h-[80vh] shadow-xl border border-slate-200 dark:border-slate-700 flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center space-x-3">
            <button
              onClick={onBack}
              className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <span className="material-symbols-outlined text-slate-500 dark:text-slate-400" style={{ fontSize: '20px' }}>
                arrow_back
              </span>
            </button>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Instruction Sets</h2>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleCreateNew}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
                add
              </span>
              <span>Create New</span>
            </button>
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                close
              </span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-slate-600 dark:text-slate-400">Loading instruction sets...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64">
              <span className="material-symbols-outlined text-red-500 mb-2" style={{ fontSize: '48px' }}>
                error
              </span>
              <p className="text-red-600 dark:text-red-400 text-center">{error}</p>
              <button
                onClick={loadInstructionSets}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : instructionSets.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <span className="material-symbols-outlined text-slate-400 mb-2" style={{ fontSize: '48px' }}>
                description
              </span>
              <p className="text-slate-600 dark:text-slate-400 text-center">No instruction sets found.</p>
            </div>
          ) : (
            <div className="p-6 overflow-y-auto h-full">
              <div className="space-y-4">
                {instructionSets.map((set) => (
                  <div
                    key={set.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      set.is_active
                        ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                    onClick={() => setSelectedSet(selectedSet?.id === set.id ? null : set)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                            Version {set.version}
                          </span>
                          {set.is_active && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
                              Active
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                          {truncateInstructions(set.instructions)}
                        </p>
                        <div className="flex items-center space-x-4 text-xs text-slate-500 dark:text-slate-400">
                          <span>Created: {formatDate(set.created_at)}</span>
                          <span>Updated: {formatDate(set.updated_at)}</span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <span className="material-symbols-outlined text-slate-400" style={{ fontSize: '16px' }}>
                          {selectedSet?.id === set.id ? 'expand_less' : 'expand_more'}
                        </span>
                      </div>
                    </div>

                    {/* Expanded content */}
                    {selectedSet?.id === set.id && (
                      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                          Full Instructions:
                        </h4>
                        <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 text-sm text-slate-700 dark:text-slate-300 max-h-48 overflow-y-auto">
                          <pre className="whitespace-pre-wrap font-mono">{set.instructions}</pre>
                        </div>
                        <div className="mt-3 flex justify-end space-x-2">
                          {!set.is_active && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const connectionId = getConnectionId();
                              if (connectionId) handleDeleteVersion(connectionId, set.version);
                            }}
                            disabled={deletingVersion === set.version}
                            className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                          >
                            {deletingVersion === set.version ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                <span>Deleting...</span>
                              </>
                            ) : (
                              <span>Delete</span>
                            )}
                          </button>
                          )}
                          {!set.is_active && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleActivateVersion(set.version);
                              }}
                              disabled={activatingVersion === set.version}
                              className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                            >
                              {activatingVersion === set.version ? (
                                <>
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                  <span>Activating...</span>
                                </>
                              ) : (
                                <span>Activate</span>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create New Instruction Set Popup */}
      {showCreatePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white dark:bg-slate-800 rounded-lg w-full max-w-2xl max-h-[80vh] shadow-xl border border-slate-200 dark:border-slate-700 flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">Create New Instruction Set</h3>
              <button
                onClick={() => setShowCreatePopup(false)}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                  close
                </span>
              </button>
            </div>

            {/* Form Content */}
            <form onSubmit={handleSubmitCreate} className="flex-1 flex flex-col">
              <div className="p-6 flex-1">
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Instructions
                  </label>
                  <button
                    type="button"
                    onClick={handleGenerateAI}
                    disabled={isGeneratingAI || isCreating}
                    className="flex items-center space-x-2 px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 transition-all"
                  >
                    {isGeneratingAI ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                          smart_toy
                        </span>
                        <span>Generate from AI</span>
                      </>
                    )}
                  </button>
                </div>
                <textarea
                  value={newInstructions}
                  onChange={(e) => setNewInstructions(e.target.value)}
                  placeholder="Enter your AI model instructions here..."
                  className="w-full h-64 px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400 resize-none"
                  required
                />
                
                {error && (
                  <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end space-x-3 p-6 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => setShowCreatePopup(false)}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating || !newInstructions.trim()}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
                        save
                      </span>
                      <span>Create</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstructionSetsList;